// What changed between two saved designs of a store's assistant, field by field, for the Versions
// compare: the state of each, not the history between them. Pure, so it is tested on its own.
import type { StoreDesign } from './api';

export type DesignValue = string | number | boolean | string[] | null;

export interface DesignChange {
  /**
   * The design field, `group.field` (`colors.header`), `translations.<language>.<field>` for words
   * in another language (`translations.de.greeting`), or `css` for the custom CSS.
   */
  field: string;
  kind: 'added' | 'removed' | 'changed';
  from: DesignValue;
  to: DesignValue;
}

// The groups in the order the design tool lists them (src/storefront/design.mjs), then the custom CSS.
const GROUP_ORDER = [
  'colors',
  'copy',
  'translations',
  'type',
  'shape',
  'launcher',
  'panel',
  'products',
  'cart',
  'logo',
];

const isFields = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

function fields(design: StoreDesign | null | undefined): Map<string, DesignValue> {
  const out = new Map<string, DesignValue>();
  const source = (design ?? {}) as Record<string, unknown>;
  for (const group of GROUP_ORDER) {
    const value = source[group];
    if (group === 'translations' || !isFields(value)) continue;
    for (const [field, inner] of Object.entries(value)) {
      const read = readValue(inner);
      if (read !== null) out.set(`${group}.${field}`, read);
    }
  }
  // Words in each other language, field by field like the store's own words.
  if (isFields(source.translations))
    for (const [language, words] of Object.entries(source.translations)) {
      if (!isFields(words)) continue;
      for (const [field, inner] of Object.entries(words)) {
        const read = readValue(inner);
        if (read !== null) out.set(`translations.${language}.${field}`, read);
      }
    }
  if (typeof source.css === 'string' && source.css.trim()) out.set('css', source.css.trim());
  return out;
}

function readValue(value: unknown): DesignValue {
  if (typeof value === 'string') return value.trim() ? value : null;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
  return null;
}

const same = (a: DesignValue, b: DesignValue) => JSON.stringify(a) === JSON.stringify(b);

/** The fields that differ between two designs, in the design tool's order. */
export function designChanges(
  from: StoreDesign | null | undefined,
  to: StoreDesign | null | undefined,
): DesignChange[] {
  const before = fields(from);
  const after = fields(to);
  const order = (field: string) => {
    const group = field.split('.')[0];
    const index = GROUP_ORDER.indexOf(group);
    return index === -1 ? GROUP_ORDER.length : index;
  };
  const keys = [...new Set([...before.keys(), ...after.keys()])].sort(
    (a, b) => order(a) - order(b),
  );
  const changes: DesignChange[] = [];
  for (const field of keys) {
    const was = before.get(field) ?? null;
    const now = after.get(field) ?? null;
    if (same(was, now)) continue;
    changes.push({
      field,
      kind: was === null ? 'added' : now === null ? 'removed' : 'changed',
      from: was,
      to: now,
    });
  }
  return changes;
}

/** A colour value the compare can show as a swatch. */
export const isColour = (value: DesignValue): boolean =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
